import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export const SettingsDialog = ({ open, onOpenChange }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/settings").then((r) => setS(r.data)).catch(() => toast.error("Failed to load settings"));
    }
  }, [open]);

  const update = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const updateFaq = (i, k, v) => {
    const faqs = [...s.faqs];
    faqs[i] = { ...faqs[i], [k]: v };
    setS({ ...s, faqs });
  };
  const addFaq = () => setS({ ...s, faqs: [...s.faqs, { id: crypto.randomUUID(), question: "", answer: "" }] });
  const removeFaq = (i) => setS({ ...s, faqs: s.faqs.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Knowledge base updated");
      onOpenChange(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col p-0" data-testid="settings-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-display text-xl">Train Ignite AI</DialogTitle>
          <p className="text-sm text-zinc-500">Configure the job summary, voice script, and approved answers the bot is allowed to give.</p>
        </DialogHeader>
        <ScrollArea className="flex-1 px-6 thin-scroll">
          <div className="space-y-5 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Company Name</Label>
                <Input data-testid="settings-company-name" value={s.company_name} onChange={(e) => update("company_name", e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Role Title</Label>
                <Input data-testid="settings-role-title" value={s.role_title} onChange={(e) => update("role_title", e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Job Summary</Label>
              <Textarea data-testid="settings-job-summary" value={s.job_summary} onChange={(e) => update("job_summary", e.target.value)} rows={4} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Voice Recording Script</Label>
              <Textarea data-testid="settings-voice-script" value={s.voice_script} onChange={(e) => update("voice_script", e.target.value)} rows={3} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">AI Behavior Instructions</Label>
              <p className="text-[11px] text-zinc-400 mt-0.5">System-level rules that govern how the bot answers (tone, what never to invent, when to escalate).</p>
              <Textarea data-testid="settings-ai-instructions" value={s.ai_instructions || ""} onChange={(e) => update("ai_instructions", e.target.value)} rows={4} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Full Call Script</Label>
              <p className="text-[11px] text-zinc-400 mt-0.5">Stored for reference. Recruiters can email this to a candidate from their profile. Not shown to the bot.</p>
              <Textarea data-testid="settings-full-script" value={s.full_script || ""} onChange={(e) => update("full_script", e.target.value)} rows={6} className="mt-1.5 font-mono text-xs" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Approved FAQs</Label>
                <Button data-testid="add-faq-btn" size="sm" variant="outline" onClick={addFaq} className="h-7 gap-1 text-xs">
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
              <div className="space-y-3">
                {s.faqs.map((f, i) => (
                  <div key={f.id} className="border border-zinc-200 rounded-lg p-3 space-y-2 relative" data-testid={`faq-row-${i}`}>
                    <Input placeholder="Question" value={f.question} onChange={(e) => updateFaq(i, "question", e.target.value)} className="text-sm" data-testid={`faq-question-${i}`} />
                    <Textarea placeholder="Approved answer" value={f.answer} onChange={(e) => updateFaq(i, "answer", e.target.value)} rows={2} className="text-sm" data-testid={`faq-answer-${i}`} />
                    <Button size="icon" variant="ghost" onClick={() => removeFaq(i)} className="absolute top-2 right-2 h-6 w-6 text-zinc-400 hover:text-red-600" data-testid={`remove-faq-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
        <DialogFooter className="px-6 py-4 border-t border-zinc-100">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="settings-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="gap-2 bg-[#E03C31] hover:bg-[#C9362C]" data-testid="settings-save">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Knowledge Base"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
